import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ACCESS_TOKEN_KEY, AUTOPATROCINIO_KEY } from '../../../core/constants/storage-keys';
import { TIPO_INSTITUTO } from '../../../core/constants/tipo-instituto';
import { VivestControl } from '../../../core/directives/vivest-control';
import { EscolhaAutopatrocinio } from '../../../core/models/autopatrocinio';
import {
  ajustarPercentual,
  limitesContribuicaoBasica,
  MESES_SUSPENSAO,
  mesmoPercentual,
  regraDependeDoSalario,
  VOLUNTARIA,
} from '../../../shared/mvp/regras/contribuicao-basica';
import { ExtractService } from '../../../shared/services/extract-service';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { LocalStorageService } from '../../../shared/services/local-storage-service';
import { formatarDecimal, formatarMoeda, lerDecimal, lerMoeda } from '../../../shared/utils/formatadores';

/** Habilita ou desabilita o controle só quando o estado muda (sem disparar valueChanges). */
function alternar(control: AbstractControl, habilitar: boolean): void {
  if (habilitar && control.disabled) {
    control.enable({ emitEvent: false });
  } else if (!habilitar && control.enabled) {
    control.disable({ emitEvent: false });
  }
}

@Component({
  // VivestControl liga os vivest-input ao formulário e aplica o vivestFormat enquanto o usuário digita.
  imports: [ReactiveFormsModule, VivestControl],
  selector: 'app-autopatrocinio',
  styleUrl: './autopatrocinio.scss',
  templateUrl: './autopatrocinio.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Autopatrocinio {
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly extractService = inject(ExtractService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);

  /** Plano do participante, lido do accessToken: value = plan, label = planDescription. */
  protected readonly plano = signal(
    this.extractService.getPlanoAccessToken(this.localStorageService.get(ACCESS_TOKEN_KEY)),
  );
  /** As regras da Contribuição Básica são buscadas pelo id do plano (o rótulo pode variar no token). */
  private readonly planoId = computed(() => this.plano()?.value);

  /**
   * Campos digitados (texto, já com a máscara do vivestFormat). A básica, a voluntária e os meses
   * começam desabilitados; os effects do construtor habilitam conforme as regras.
   */
  readonly form = this.formBuilder.nonNullable.group({
    salario: [''],
    basica: [{ value: '', disabled: true }],
    voluntaria: [{ value: '', disabled: true }],
    meses: [{ value: '', disabled: true }],
  });

  // Campos (texto exibido em tela)
  protected readonly salarioTexto = toSignal(this.form.controls.salario.valueChanges, { initialValue: '' });
  protected readonly basicaTexto = toSignal(this.form.controls.basica.valueChanges, { initialValue: '' });
  protected readonly voluntariaTexto = toSignal(this.form.controls.voluntaria.valueChanges, { initialValue: '' });
  protected readonly mesesTexto = toSignal(this.form.controls.meses.valueChanges, { initialValue: '' });
  protected readonly cancelarContribuicoes = signal(false);
  protected readonly suspenderContribuicoes = signal(false);

  // Valores numéricos
  protected readonly salario = computed(() => lerMoeda(this.salarioTexto()) ?? 0);
  protected readonly basica = computed(() =>
    this.cancelarContribuicoes() ? 0 : (lerDecimal(this.basicaTexto()) ?? 0),
  );
  protected readonly voluntaria = computed(() =>
    this.voluntariaHabilitada() ? (lerDecimal(this.voluntariaTexto()) ?? 0) : 0,
  );

  /** Planos cuja regra depende do salário só liberam a Básica depois do salário informado. */
  protected readonly aguardandoSalario = computed(
    () => regraDependeDoSalario(this.planoId()) && this.salario() <= 0,
  );

  protected readonly limitesBasica = computed(() =>
    limitesContribuicaoBasica(this.planoId(),this.salario()),
  );

  protected readonly casasPercentual = computed(() => {
    const escala = this.limitesBasica()?.escala ?? 0.01;
    return escala >= 0.1 ? 1 : 2;
  });

  protected readonly basicaDesabilitada = computed(
    () => this.cancelarContribuicoes() || this.aguardandoSalario() || !this.limitesBasica(),
  );

  /** A Voluntária só é liberada quando a Básica está no máximo permitido. */
  protected readonly voluntariaHabilitada = computed(() => {
    const limites = this.limitesBasica();
    const basica = lerDecimal(this.basicaTexto());
    return (
      !this.cancelarContribuicoes() &&
      !!limites &&
      basica != null &&
      mesmoPercentual(basica, limites.maximo)
    );
  });

  // Cálculo da contribuição mensal
  protected readonly valorBasicaParticipante = computed(
    () => (this.salario() * this.basica()) / 100,
  );
  protected readonly valorBasicaTotal = computed(() => this.valorBasicaParticipante() * 2);
  protected readonly valorVoluntaria = computed(() => (this.salario() * this.voluntaria()) / 100);
  protected readonly valorTotal = computed(() => this.valorBasicaTotal() + this.valorVoluntaria());

  protected readonly meses = computed(() => {
    const meses = Number(this.mesesTexto());
    return Number.isInteger(meses) ? meses : 0;
  });

  protected readonly podeContinuar = computed(() => {
    if (this.salario() <= 0) {
      return false;
    }
    const contribuicaoOk =
      this.cancelarContribuicoes() ||
      (lerDecimal(this.basicaTexto()) != null && !!this.limitesBasica());
    const suspensaoOk =
      !this.suspenderContribuicoes() ||
      (this.meses() >= MESES_SUSPENSAO.minimo && this.meses() <= MESES_SUSPENSAO.maximo);
    return contribuicaoOk && suspensaoOk;
  });

  // Formatação para o template
  protected readonly moeda = formatarMoeda;
  protected percentual(valor: number): string {
    return `${formatarDecimal(valor, this.casasPercentual())}%`;
  }

  constructor() {
    // Os vivest-input ligados ao form são habilitados/desabilitados pelo controle (VivestControl).
    const { basica, voluntaria, meses } = this.form.controls;
    effect(() => alternar(basica, !this.basicaDesabilitada()));
    effect(() => alternar(voluntaria, this.voluntariaHabilitada()));
    effect(() => alternar(meses, this.suspenderContribuicoes()));

    this.carregarSalario();
  }

  // ---- Salário ----
  /**
   * Preenche o salário com a base de cálculo do autopatrocínio, buscada com os dados do
   * participante do accessToken, e desabilita o campo. Sem token, sem salário no retorno ou com
   * erro, o campo fica vazio e editável para ser digitado.
   */
  private async carregarSalario(): Promise<void> {
    const participante = this.extractService.getParticipanteAccessToken(
      this.localStorageService.get(ACCESS_TOKEN_KEY),
    );
    if (!participante) {
      return;
    }

    this.loaderService.showLoader('Carregando...');
    try {
      const { body } = await this.httpClientService.getSalarioMVP(participante);
      const baseCalculo = body?.planoParticipante?.contribuicaoDesligado?.autopatrocinio?.baseCalculo;
      // Com a base de cálculo retornada, o salário vem preenchido e fica bloqueado para edição.
      if (baseCalculo != null && baseCalculo > 0) {
        const { salario } = this.form.controls;
        salario.setValue(formatarMoeda(baseCalculo));
        salario.disable({ emitEvent: false });
        // Se o usuário já tinha digitado a Básica, reaplica os limites da faixa do salário retornado.
        this.onSalarioBlur();
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  protected onSalarioBlur(): void {
    // A faixa salarial pode ter mudado: reaplica os limites na Básica já digitada.
    this.onBasicaBlur();
  }

  // ---- Cancelar contribuições ----
  /** O vivest-switch dispara `change` e expõe o estado na propriedade `checked`. */
  protected onCancelarChange(event: Event): void {
    const cancelar = (event.target as HTMLElement & { checked: boolean }).checked;
    const { basica, voluntaria, meses } = this.form.controls;
    this.cancelarContribuicoes.set(cancelar);
    if (cancelar) {
      basica.setValue(formatarDecimal(0));
      voluntaria.setValue(formatarDecimal(0));
      // Cancelar e suspender são excludentes: desliga a suspensão e limpa os meses.
      this.suspenderContribuicoes.set(false);
      meses.setValue('');
    } else {
      basica.setValue('');
      voluntaria.setValue('');
    }
  }

  // ---- Contribuição Básica ----
  protected onBasicaBlur(): void {
    const limites = this.limitesBasica();
    const valor = lerDecimal(this.basicaTexto());
    if (this.cancelarContribuicoes() || !limites || valor == null) {
      return;
    }
    const ajustado = ajustarPercentual(valor, limites);
    this.form.controls.basica.setValue(formatarDecimal(ajustado));
    if (!mesmoPercentual(ajustado, limites.maximo)) {
      this.form.controls.voluntaria.setValue('');
    }
  }

  // ---- Contribuição Voluntária ----
  protected onVoluntariaBlur(): void {
    const valor = lerDecimal(this.voluntariaTexto());
    if (!this.voluntariaHabilitada() || valor == null) {
      return;
    }
    this.form.controls.voluntaria.setValue(formatarDecimal(ajustarPercentual(valor, VOLUNTARIA)));
  }

  // ---- Suspensão temporária ----
  protected onSuspenderChange(event: Event): void {
    const suspender = (event.target as HTMLElement & { checked: boolean }).checked;
    this.suspenderContribuicoes.set(suspender);
    if (!suspender) {
      this.form.controls.meses.setValue('');
    }
  }

  protected onMesesBlur(): void {
    const meses = Number(this.mesesTexto());
    if (!this.mesesTexto() || !Number.isFinite(meses)) {
      return;
    }
    const ajustado = Math.min(
      MESES_SUSPENSAO.maximo,
      Math.max(MESES_SUSPENSAO.minimo, Math.round(meses)),
    );
    this.form.controls.meses.setValue(String(ajustado));
  }

  // ---- Navegação ----
  onReturn(): void {
    this.router.navigate(['/instituto-online/escolha-instituto']);
  }

  /** Salva as escolhas no localStorage (AUTOPATROCINIO_KEY) para a assinatura e segue para o termo. */
  onSubmit(): void {
    if (!this.podeContinuar()) {
      return;
    }
    this.localStorageService.addObjectStorageEcrypted<EscolhaAutopatrocinio>(
      {
        salario: this.salario(),
        cancelarContribuicoes: this.cancelarContribuicoes(),
        percentualBasica: this.basica(),
        percentualVoluntaria: this.voluntaria(),
        valorBasica: this.valorBasicaTotal(),
        valorVoluntaria: this.valorVoluntaria(),
        valorTotal: this.valorTotal(),
        mesesSuspensao: this.suspenderContribuicoes() ? this.meses() : null,
      },
      AUTOPATROCINIO_KEY,
    );
    this.router.navigate(['/instituto-online/assinatura-termo-mvp'], {
      queryParams: { idInstituto: TIPO_INSTITUTO.AUTOPATROCINIO },
    });
  }
}
